import { Controller } from "@hotwired/stimulus"

// Story B — drag a tree node onto another node.
//
// We attach pointerdown to the controller's container (the tree-graph wrapper).
// d3-zoom is configured with a `filter` that rejects events whose target is a
// tree-node, so the canvas does not pan when the user starts on a node — but
// the native click still fires (no preventDefault here), so the popover keeps
// working for short clicks.

const LONG_PRESS_MS = 250
const DRAG_THRESHOLD_PX = 5

export default class extends Controller {
  static values = { treeId: Number }

  connect() {
    this._onPointerDown = this.onPointerDown.bind(this)
    this._onPointerMove = this.onPointerMove.bind(this)
    this._onPointerUp = this.onPointerUp.bind(this)
    this.element.addEventListener("pointerdown", this._onPointerDown)
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this._onPointerDown)
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    this.cleanup()
  }

  onPointerDown(event) {
    const sourceNode = event.target.closest(".tree-node[data-person-id]")
    if (!sourceNode) return
    if (event.target.closest(".tree-node__quick-add, .tree-node__collapse-btn")) return

    this.sourceNode = sourceNode
    this.sourceId = sourceNode.getAttribute("data-person-id")
    this.startX = event.clientX
    this.startY = event.clientY
    this.dragActive = false

    this._longPressTimer = setTimeout(() => this.activateDrag(), LONG_PRESS_MS)
    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp)
  }

  activateDrag() {
    if (!this.sourceNode) return
    this.dragActive = true
    this.sourceNode.classList.add("tree-node--dragging")
    this.ghost = this.createGhost()
    document.body.appendChild(this.ghost)
    this.highlightDropTargets()
  }

  createGhost() {
    const svgNS = "http://www.w3.org/2000/svg"
    const ghost = document.createElementNS(svgNS, "svg")
    ghost.classList.add("tree-drag-ghost")
    ghost.setAttribute("width", "160")
    ghost.setAttribute("height", "140")
    ghost.setAttribute("viewBox", "-80 -70 160 140")
    ghost.style.position = "fixed"
    ghost.style.pointerEvents = "none"
    ghost.style.zIndex = "1000"
    ghost.style.overflow = "visible"

    // Carry over <defs> (gradients used by patriarch/matriarch nodes)
    const origDefs = this.sourceNode.ownerSVGElement?.querySelector("defs")
    if (origDefs) ghost.appendChild(origDefs.cloneNode(true))

    // Clone the source node group and reset its transform to (0,0)
    const cloned = this.sourceNode.cloneNode(true)
    cloned.setAttribute("transform", "translate(0,0)")
    cloned.querySelectorAll(".tree-node__quick-add").forEach(n => n.remove())
    cloned.classList.remove("tree-node--dragging")
    ghost.appendChild(cloned)

    ghost.style.left = `${this.startX - 80 + 14}px`
    ghost.style.top  = `${this.startY - 70 + 14}px`
    this._ghostOffsetX = -80 + 14
    this._ghostOffsetY = -70 + 14
    return ghost
  }

  highlightDropTargets() {
    this.element.querySelectorAll(".tree-node[data-person-id]").forEach(n => {
      if (n.getAttribute("data-person-id") !== this.sourceId) {
        n.classList.add("tree-node--drop-target")
      }
    })
  }

  clearDropTargets() {
    this.element.querySelectorAll(".tree-node--drop-target").forEach(n => {
      n.classList.remove("tree-node--drop-target", "tree-node--drop-hover")
    })
  }

  updateHoverTarget(event) {
    const drop = this.findDropTarget(event)
    this.element.querySelectorAll(".tree-node--drop-hover").forEach(n => {
      n.classList.remove("tree-node--drop-hover")
    })
    if (drop && drop.targetId !== this.sourceId) {
      const targetNode = this.element.querySelector(
        `.tree-node[data-person-id="${drop.targetId}"]`
      )
      targetNode?.classList.add("tree-node--drop-hover")
    }
  }

  onPointerMove(event) {
    if (!this.dragActive && this.sourceNode) {
      const dx = event.clientX - this.startX
      const dy = event.clientY - this.startY
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        if (this._longPressTimer) clearTimeout(this._longPressTimer)
        this.activateDrag()
      }
    }
    if (this.dragActive && this.ghost) {
      this.ghost.style.left = `${event.clientX + this._ghostOffsetX}px`
      this.ghost.style.top  = `${event.clientY + this._ghostOffsetY}px`
      this.updateHoverTarget(event)
    }
  }

  onPointerUp(event) {
    if (this._longPressTimer) clearTimeout(this._longPressTimer)
    if (!this.dragActive) { this.cleanup(); return }

    const drop = this.findDropTarget(event)
    if (drop && drop.targetId !== this.sourceId) {
      this.dispatch("dropped", { detail: { ...drop, sourceId: this.sourceId } })
    }
    this.cleanup()
  }

  findDropTarget(event) {
    const el = document.elementFromPoint(event.clientX, event.clientY)
    if (!el) return null
    const targetNode = el.closest(".tree-node[data-person-id]")
    if (!targetNode) return null
    const targetId = targetNode.getAttribute("data-person-id")
    return { targetId, zone: this.zoneFor(event, targetNode) }
  }

  zoneFor(event, targetNode) {
    const rect = targetNode.getBoundingClientRect()
    const dx = event.clientX - (rect.left + rect.width / 2)
    const dy = event.clientY - (rect.top + rect.height / 2)
    if (Math.abs(dy) > Math.abs(dx)) return dy < 0 ? "top" : "bottom"
    return dx < 0 ? "left" : "right"
  }

  cleanup() {
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    if (this._longPressTimer) clearTimeout(this._longPressTimer)
    if (this.sourceNode) this.sourceNode.classList.remove("tree-node--dragging")
    if (this.ghost && this.ghost.parentNode) this.ghost.parentNode.removeChild(this.ghost)
    this.clearDropTargets()
    this.dragActive = false
    this.sourceId = null
    this.sourceNode = null
    this.ghost = null
    this._longPressTimer = null
  }
}
